import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import Wheel from "@/components/custom/Wheel";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Toaster, toast } from "sonner";
import confetti from "canvas-confetti";
import { Volume2, VolumeX, Plus, X, History, Settings } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_CUISINES = [
  "American", "Middle Eastern", "Japanese", "Korean", "Vietnamese",
  "Italian", "Mexican", "Indian", "Thai", "Mediterranean", "German", "Chinese"
];

function App() {
  const [cuisines, setCuisines] = useState([]);
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [winner, setWinner] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [spinHistory, setSpinHistory] = useState([]);
  const [newCuisine, setNewCuisine] = useState("");
  const [showCuisineDialog, setShowCuisineDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  const [loadingRestaurants, setLoadingRestaurants] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    const savedCuisines = localStorage.getItem('cuisines');
    const savedSound = localStorage.getItem('soundEnabled');
    
    if (savedCuisines) {
      setCuisines(JSON.parse(savedCuisines));
    } else {
      setCuisines(DEFAULT_CUISINES);
      localStorage.setItem('cuisines', JSON.stringify(DEFAULT_CUISINES));
    }
    
    if (savedSound !== null) {
      setSoundEnabled(savedSound === 'true');
    }
    
    fetchSpinHistory();
    getUserLocation();
  }, []);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.log('Location error:', error);
          // Default to San Francisco if location access denied
          setUserLocation({ latitude: 37.7749, longitude: -122.4194 });
        }
      );
    } else {
      setUserLocation({ latitude: 37.7749, longitude: -122.4194 });
    }
  };

  const fetchSpinHistory = async () => {
    try {
      const response = await axios.get(`${API}/spin-history`);
      setSpinHistory(response.data);
    } catch (error) {
      console.error('Error fetching spin history:', error);
    }
  };

  const saveSpinResult = async (cuisine) => {
    try {
      await axios.post(`${API}/spin`, { cuisine });
      fetchSpinHistory();
    } catch (error) {
      console.error('Error saving spin result:', error);
    }
  };

  const playTickSound = () => {
    if (!soundEnabled) return;
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.05);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.05);
  };

  const playWinSound = () => {
    if (!soundEnabled) return;
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 523.25;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
    
    setTimeout(() => {
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      osc2.frequency.value = 659.25;
      gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      osc2.start(audioContext.currentTime);
      osc2.stop(audioContext.currentTime + 0.5);
    }, 100);
  };

  const handleSegmentChange = () => {
    playTickSound();
  };

  const searchRestaurants = async (cuisine) => {
    if (!userLocation) {
      console.log('No location available');
      return;
    }

    setLoadingRestaurants(true);

    try {
      const response = await axios.post(`${API}/restaurants/search`, {
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
        cuisine: cuisine,
        radius: 5000
      });

      setRestaurants(response.data.restaurants || []);
    } catch (error) {
      console.error('Error searching restaurants:', error);
      setRestaurants([]);
    } finally {
      setLoadingRestaurants(false);
    }
  };

  const spinWheel = () => {
    if (isSpinning || cuisines.length === 0) return;
    
    setIsSpinning(true);
    setWinner(null);
    setRestaurants([]);
    
    const randomSpins = 5 + Math.floor(Math.random() * 3);
    const randomDegree = Math.floor(Math.random() * 360);
    const totalRotation = randomSpins * 360 + randomDegree;
    
    const duration = 4000;
    const startTime = Date.now();
    const startRotation = rotation;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeOut = 1 - Math.pow(1 - progress, 4);
      const currentRotation = startRotation + totalRotation * easeOut;
      
      setRotation(currentRotation);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIsSpinning(false);
        
        // Calculate winner - arrow at 3 o'clock, but winner is 90° counterclockwise (at 12 o'clock)
        const finalRotation = currentRotation % 360;
        const anglePerSegment = 360 / cuisines.length;
        
        // Arrow at 90° (3 o'clock), adjusted counterclockwise by 90° = 0° (12 o'clock)
        const adjustedRotation = (360 - finalRotation) % 360;
        const winningIndex = Math.round(adjustedRotation / anglePerSegment) % cuisines.length;
        const winningCuisine = cuisines[winningIndex];
        
        setWinner(winningCuisine);
        saveSpinResult(winningCuisine);
        searchRestaurants(winningCuisine);
        playWinSound();
        
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#FF4785', '#FFD600', '#00D4FF', '#00FF94', '#D946EF', '#FF8F00']
        });
        
        toast.success(`You're eating ${winningCuisine} tonight! 🎉`);
      }
    };
    
    animate();
  };

  const addCuisine = () => {
    if (!newCuisine.trim()) return;
    
    if (cuisines.includes(newCuisine.trim())) {
      toast.error('This cuisine is already in the wheel!');
      return;
    }
    
    const updated = [...cuisines, newCuisine.trim()];
    setCuisines(updated);
    localStorage.setItem('cuisines', JSON.stringify(updated));
    setNewCuisine("");
    toast.success(`${newCuisine} added to the wheel!`);
  };

  const removeCuisine = (cuisine) => {
    if (cuisines.length <= 2) {
      toast.error('You need at least 2 cuisines in the wheel!');
      return;
    }
    
    const updated = cuisines.filter(c => c !== cuisine);
    setCuisines(updated);
    localStorage.setItem('cuisines', JSON.stringify(updated));
    toast.success(`${cuisine} removed from the wheel`);
  };

  const toggleSound = (checked) => {
    setSoundEnabled(checked);
    localStorage.setItem('soundEnabled', checked.toString());
  };

  return (
    <div className="min-h-screen bg-[#FFFBEB] p-4 md:p-8">
      <Toaster position="top-center" />
      
      <div className="max-w-2xl mx-auto">
        <header className="text-center mb-8">
          <h1 
            className="text-4xl md:text-6xl font-black tracking-tight mb-4"
            style={{ fontFamily: '"Fredoka", sans-serif' }}
          >
            My Meal Roulette
          </h1>
          <p 
            className="text-lg md:text-xl text-slate-700"
            style={{ fontFamily: '"Quicksand", sans-serif', fontWeight: 600 }}
          >
            Can't decide what to eat? Let the wheel decide! 🍕
          </p>
        </header>

        <div className="flex justify-center gap-4 mb-8">
          <Dialog open={showCuisineDialog} onOpenChange={setShowCuisineDialog}>
            <DialogTrigger asChild>
              <Button 
                data-testid="manage-cuisines-btn"
                className="bg-[#FFD600] text-black hover:bg-[#FFD600]/90 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-1 active:shadow-[0px_0px_0px_0px_rgba(0,0,0,1)] active:translate-x-1 font-bold"
              >
                <Settings className="mr-2 h-5 w-5" strokeWidth={3} />
                Manage Cuisines
              </Button>
            </DialogTrigger>
            <DialogContent className="border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]" aria-describedby="manage-cuisines-description">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: '"Fredoka", sans-serif' }} className="text-2xl">
                  Manage Your Cuisines
                </DialogTitle>
              </DialogHeader>
              <div id="manage-cuisines-description" className="sr-only">
                Add or remove cuisines from the wheel
              </div>
              
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    data-testid="add-cuisine-input"
                    value={newCuisine}
                    onChange={(e) => setNewCuisine(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addCuisine()}
                    placeholder="Add new cuisine..."
                    className="border-2 border-black"
                  />
                  <Button 
                    data-testid="add-cuisine-btn"
                    onClick={addCuisine}
                    className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
                  >
                    <Plus strokeWidth={3} />
                  </Button>
                </div>
                
                <ScrollArea className="h-[300px] border-2 border-black rounded-lg p-4">
                  <div className="space-y-2">
                    {cuisines.map((cuisine) => (
                      <div 
                        key={cuisine}
                        data-testid={`cuisine-item-${cuisine.toLowerCase().replace(/\s+/g, '-')}`}
                        className="flex items-center justify-between p-3 bg-white border-2 border-black rounded-lg shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
                      >
                        <span className="font-bold" style={{ fontFamily: '"Quicksand", sans-serif' }}>
                          {cuisine}
                        </span>
                        <Button
                          data-testid={`remove-cuisine-${cuisine.toLowerCase().replace(/\s+/g, '-')}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCuisine(cuisine)}
                          className="hover:bg-red-100"
                        >
                          <X className="h-4 w-4" strokeWidth={3} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
            <DialogTrigger asChild>
              <Button 
                data-testid="view-history-btn"
                className="bg-[#00D4FF] text-black hover:bg-[#00D4FF]/90 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-1 active:shadow-[0px_0px_0px_0px_rgba(0,0,0,1)] active:translate-x-1 font-bold"
              >
                <History className="mr-2 h-5 w-5" strokeWidth={3} />
                History
              </Button>
            </DialogTrigger>
            <DialogContent className="border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]" aria-describedby="spin-history-description">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: '"Fredoka", sans-serif' }} className="text-2xl">
                  Spin History (Last 20)
                </DialogTitle>
              </DialogHeader>
              <div id="spin-history-description" className="sr-only">
                View your recent spin results
              </div>
              
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {spinHistory.length === 0 ? (
                    <p className="text-center text-slate-600 py-8">No spins yet! Try your luck! 🎰</p>
                  ) : (
                    spinHistory.map((spin, index) => (
                      <div 
                        key={spin.id}
                        data-testid={`history-item-${index}`}
                        className="flex items-center justify-between p-3 bg-white border-2 border-black rounded-lg shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] mb-2"
                      >
                        <span className="font-bold text-lg" style={{ fontFamily: '"Quicksand", sans-serif' }}>
                          {spin.cuisine}
                        </span>
                        <span className="text-sm text-slate-600">
                          {new Date(spin.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white border-4 border-black rounded-2xl shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 md:p-8 mb-6">
          <div className="flex justify-center mb-8">
            <Wheel 
              cuisines={cuisines}
              isSpinning={isSpinning}
              rotation={rotation}
              onSegmentChange={handleSegmentChange}
            />
          </div>

          <div className="text-center">
            <Button
              data-testid="spin-button"
              onClick={spinWheel}
              disabled={isSpinning || cuisines.length === 0}
              className="w-64 h-20 text-3xl font-black uppercase tracking-widest bg-[#FF4785] text-white border-4 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[6px] active:translate-x-[6px] rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: '"Fredoka", sans-serif' }}
            >
              {isSpinning ? 'SPINNING...' : 'SPIN!'}
            </Button>

            {winner && (
              <div 
                data-testid="winner-announcement"
                className="mt-6 p-4 bg-[#FFD600] border-4 border-black rounded-xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] animate-bounce"
              >
                <p className="text-2xl font-black" style={{ fontFamily: '"Fredoka", sans-serif' }}>
                  🎉 {winner}! 🎉
                </p>
              </div>
            )}
          </div>
        </div>

        {winner && (
          <div className="bg-white border-4 border-black rounded-2xl shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 md:p-8 mb-6">
            <h2 className="text-3xl font-black mb-4" style={{ fontFamily: '"Fredoka", sans-serif' }}>
              🍽️ Nearby {winner} Restaurants
            </h2>
            
            {loadingRestaurants ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#FF4785]"></div>
              </div>
            ) : restaurants.length === 0 ? (
              <div className="text-center py-8 px-4 bg-[#FFFBEB] border-2 border-black rounded-xl">
                <p className="text-lg font-bold mb-2" style={{ fontFamily: '"Quicksand", sans-serif' }}>
                  No restaurants found nearby
                </p>
                <p className="text-sm text-slate-600">
                  Try enabling location permissions or the Google Places API might need activation
                </p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {restaurants.map((restaurant) => (
                  <a
                    key={restaurant.place_id}
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.name + ' ' + restaurant.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-4 bg-white border-3 border-black rounded-xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-bold flex-1" style={{ fontFamily: '"Quicksand", sans-serif' }}>
                        {restaurant.name}
                      </h3>
                      {restaurant.rating && (
                        <span className="ml-2 px-2 py-1 bg-[#FFD600] border-2 border-black rounded-lg text-sm font-bold">
                          ⭐ {restaurant.rating}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-slate-600 mb-2">
                      {restaurant.address}
                    </p>
                    
                    <div className="flex items-center gap-2 flex-wrap">
                      {restaurant.price_level && (
                        <span className="text-sm font-bold text-[#00FF94]">
                          {'$'.repeat(restaurant.price_level)}
                        </span>
                      )}
                      {restaurant.opening_hours?.open_now !== undefined && (
                        <span className={`text-xs px-2 py-1 border-2 border-black rounded font-bold ${
                          restaurant.opening_hours.open_now ? 'bg-[#00FF94]' : 'bg-[#FF4785] text-white'
                        }`}>
                          {restaurant.opening_hours.open_now ? 'Open Now' : 'Closed'}
                        </span>
                      )}
                      {restaurant.user_ratings_total && (
                        <span className="text-xs text-slate-500">
                          ({restaurant.user_ratings_total} reviews)
                        </span>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-center gap-3 p-4 bg-white border-2 border-black rounded-xl shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
          <Label 
            htmlFor="sound-toggle" 
            className="flex items-center gap-2 font-bold cursor-pointer"
            style={{ fontFamily: '"Quicksand", sans-serif' }}
          >
            {soundEnabled ? <Volume2 strokeWidth={3} /> : <VolumeX strokeWidth={3} />}
            Sound Effects
          </Label>
          <Switch
            data-testid="sound-toggle"
            id="sound-toggle"
            checked={soundEnabled}
            onCheckedChange={toggleSound}
            className="data-[state=checked]:bg-[#00FF94] data-[state=unchecked]:bg-slate-300 border-2 border-black"
          />
        </div>
      </div>
    </div>
  );
}

export default App;